import React, { useState } from "react";
import { EmailContext } from "../../types";
import styles from "./ContextStrip.module.css";

interface ContextStripProps {
  context: EmailContext | null;
}

// Convert HTML email body to plain text while preserving paragraph breaks.
// Graph returns raw HTML — we replace block-level tags with newlines first so
// the resulting text mirrors the original message structure instead of
// collapsing into a single wall of words.
function htmlBodyToText(html: string): string {
  if (!html) return "";
  let s = html
    // Drop content that never produces visible text.
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    // Convert structural tags to line breaks.
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ");

  // Parse what's left so the browser decodes entities (&amp; &nbsp; etc.) and
  // strips the remaining tags safely.
  try {
    const text = new DOMParser().parseFromString(s, "text/html").body.textContent ?? "";
    s = text;
  } catch {
    s = s.replace(/<[^>]+>/g, "");
  }

  // Normalise: collapse 3+ newlines to 2, trim each line, drop empty trailing
  // whitespace, but keep paragraph breaks intact.
  return s
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Outlook embeds the entire reply chain inside each message's body. To show
// only the message the sender actually typed, strip everything from the first
// quoted-reply marker onwards. Same heuristics as the compose body stripper.
function stripQuotedReply(text: string): string {
  if (!text) return "";
  const patterns: RegExp[] = [
    /-----+\s*Original Message\s*-----+/i,
    /________________________+/,
    /^\s*From:\s.+$/m,
    /^\s*On\s.+?wrote:\s*$/im,
    /^\s*Le\s.+?a écrit\s*:\s*$/im,
    /^\s*Am\s.+?schrieb\s.+?:\s*$/im,
  ];
  let earliest = -1;
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m.index !== undefined && (earliest === -1 || m.index < earliest)) {
      earliest = m.index;
    }
  }
  return (earliest >= 0 ? text.slice(0, earliest) : text).trim();
}

function bodyToDisplay(html: string): string {
  return stripQuotedReply(htmlBodyToText(html));
}

function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function ContextStrip({ context }: ContextStripProps) {
  const [open, setOpen] = useState(false);

  if (!context) {
    return (
      <div className={styles.strip}>
        <div className={styles.checkBadge} style={{ background: "#9a9a9a" }}>i</div>
        <div className={styles.content}>
          <p className={styles.title} style={{ color: "#6b6b6b" }}>No email open</p>
          <p className={styles.noEmail}>Open an email in Outlook to get started</p>
        </div>
      </div>
    );
  }

  const draftLabel = context.hasDraft
    ? context.isReply
      ? "Reply draft detected"
      : "New draft detected"
    : null;

  const subtitle =
    context.threadLength > 0
      ? `${context.threadLength} message${context.threadLength !== 1 ? "s" : ""} with ${context.recipientName}` +
        (context.opportunityLabel ? ` · ${context.opportunityLabel}` : "") +
        (context.engagementLabel ? ` · ${context.engagementLabel}` : "")
      : context.hasDraft
      ? "New email — no previous thread"
      : "Open an email in Outlook to get started";

  // Display messages newest-first
  const messages = context.messages ? [...context.messages].reverse() : null;

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styles.strip} ${styles.stripClickable}`}
        onClick={() => setOpen((v) => !v)}
        type="button"
        aria-expanded={open}
      >
        <div className={styles.checkBadge}>✓</div>
        <div className={styles.content}>
          <div className={styles.titleRow}>
            <p className={styles.title}>
              {context.threadLength > 0 ? "Email thread read" : "Compose ready"}
            </p>
            {draftLabel && (
              <span className={styles.draftBadge}>✎ {draftLabel}</span>
            )}
          </div>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>▾</span>
      </button>

      {open && (
        <div className={styles.detail}>
          {/* Subject + basic meta */}
          {context.subject && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Subject</span>
              <span className={styles.metaValue}>{context.subject}</span>
            </div>
          )}
          {context.receivedAt && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Latest</span>
              <span className={styles.metaValue}>{formatDate(context.receivedAt)}</span>
            </div>
          )}

          <div className={styles.divider} />

          {/* Thread messages */}
          {messages && messages.length > 0 ? (
            <div className={styles.threadList}>
              {messages.map((msg, idx) => {
                const plain = bodyToDisplay(msg.body);
                return (
                  <MessageBubble
                    key={idx}
                    sender={msg.sender || msg.senderEmail}
                    email={msg.senderEmail}
                    time={formatTime(msg.receivedAt)}
                    body={plain}
                    index={messages.length - idx}
                  />
                );
              })}
            </div>
          ) : (
            /* Fallback if no messages array (DOM-scraped context) */
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>From</span>
              <span className={styles.metaValue}>
                {context.sender}
                {context.senderEmail && (
                  <span className={styles.emailAddr}> ‹{context.senderEmail}›</span>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface BubbleProps {
  sender: string;
  email: string;
  time: string;
  body: string;
  index: number;
}

function MessageBubble({ sender, email, time, body, index }: BubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 200;
  const truncated = body.length > LIMIT && !expanded;
  const display = truncated ? body.slice(0, LIMIT) + "…" : body;

  return (
    <div className={styles.bubble}>
      <div className={styles.bubbleHeader}>
        <div className={styles.bubbleMeta}>
          <span className={styles.bubbleSender}>{sender || email}</span>
          {sender && email && <span className={styles.bubbleEmail}>‹{email}›</span>}
        </div>
        <span className={styles.bubbleTime}>{time}</span>
      </div>
      <p className={styles.bubbleBody}>{display}</p>
      {body.length > LIMIT && (
        <button
          className={styles.showMore}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          type="button"
        >
          {expanded ? "Show less ▴" : "Show more ▾"}
        </button>
      )}
    </div>
  );
}
